import moment from 'moment';
import { AccordionDetails, CircularProgress, Grid, ThemeProvider, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import React from 'react';
import { CacheProvider } from '@emotion/react';
import { useFormikContext } from 'formik';
import FinalDateDetails from '../../FinalDateDetails';
import { cacheLtr, cacheRtl } from '../../../../../common/GeneralUtils';
import { Accordion, AccordionSummary, AccordionSummaryVariant } from '../Accordion';
import { EditOrderFormValues, EditOrderPanels } from '../../EditOrderDialog';
import { DateAvailabilityOut, OrderPageTypes } from '../../../../../api';
import { SharedMobileTimePicker } from '../../../../../common/ui/SharedMobileTimePicker';
import { useEditOrder } from '../../EditOrderContext';
import { DateAndEventTimeFields } from './DateAndEventTimeFields';
import { PrefillButton } from '../../externalPrefill/PrefillButton';

type DateAndTimePanelProps = {
  availableDatesForEvents: DateAvailabilityOut[];
  isLoadingDates: boolean;
  expanded?: EditOrderPanels;
  handleChange: any;
};

export const DateAndTimePanel = ({
  availableDatesForEvents,
  isLoadingDates,
  expanded,
  handleChange,
}: DateAndTimePanelProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { i18n } = useTranslation();
  const dir = i18n.dir();
  const { values, setFieldValue } = useFormikContext<EditOrderFormValues>();
  // @ts-ignore
  const { editOrderState } = useEditOrder();

  const isEventOrCourse = [OrderPageTypes.EVENT, OrderPageTypes.COURSE].includes(
    editOrderState.orderPage.order_page_type,
  );
  const duration = isEventOrCourse
    ? editOrderState.details.duration
    : values.privateOrderEndTimeSeconds - values.timeSeconds;
  const hasNoDates = isEventOrCourse && !isLoadingDates && availableDatesForEvents.length === 0;
  const dateValue = moment(values.date);
  const { timeSeconds, privateOrderEndTimeSeconds } = values;

  return (
    <Accordion
      onChange={handleChange}
      expandedPanelName={expanded}
      panelName={EditOrderPanels.DateAndTimePanel}
      allOpenValue={EditOrderPanels.AllOpen}>
      <AccordionSummary
        variant={AccordionSummaryVariant.CAPTION}
        title={t('calendar.edit_order.dateandtime')}
        summaryComponent={
          hasNoDates ? (
            <Typography variant="caption" color="warning.main">
              {t('calendar.edit_order.no_available_dates')}
            </Typography>
          ) : (
            <FinalDateDetails date={dateValue} timeSeconds={timeSeconds} duration={duration} />
          )
        }
      />
      <AccordionDetails>
        {isLoadingDates ? (
          <Grid container justifyContent="center" py={2}>
            <CircularProgress size={24} />
          </Grid>
        ) : hasNoDates ? (
          <Typography variant="body2" color="text.secondary">
            {t('calendar.edit_order.no_available_dates')}
          </Typography>
        ) : (
          <Grid container item gap={2} flexWrap="nowrap" alignItems="center">
            <DateAndEventTimeFields availableDatesForEvents={availableDatesForEvents} />
            {!isEventOrCourse && (
              <CacheProvider value={dir === 'rtl' ? cacheRtl : cacheLtr}>
                <ThemeProvider theme={{ ...theme, direction: dir === 'rtl' ? 'rtl' : 'ltr' }}>
                  <Grid container item gap={2} flexWrap="nowrap">
                    <Grid item maxWidth={100}>
                      <SharedMobileTimePicker
                        minTime={moment().set({ hour: 6, minute: 0, second: 0, millisecond: 0 })}
                        label={t('calendar.edit_order.start_time')}
                        value={moment.unix(timeSeconds).tz('utc')}
                        minutesStep={15}
                        onChange={(newValue) => {
                          const val = newValue
                            ? newValue.utc().hours() * 3600 + newValue.utc().minutes() * 60
                            : timeSeconds;
                          void setFieldValue('timeSeconds', val);
                          void setFieldValue('privateOrderEndTimeSeconds', val + duration);
                        }}
                      />
                    </Grid>
                    <Grid item maxWidth={100}>
                      <SharedMobileTimePicker
                        minTime={moment().set({ hour: 6, minute: 0, second: 0, millisecond: 0 })}
                        label={t('calendar.edit_order.end_time')}
                        value={moment.unix(privateOrderEndTimeSeconds).tz('utc')}
                        minutesStep={15}
                        onChange={(newValue) => {
                          void setFieldValue(
                            'privateOrderEndTimeSeconds',
                            newValue ? newValue.utc().hours() * 3600 + newValue.utc().minutes() * 60 : timeSeconds,
                          );
                        }}
                      />
                    </Grid>
                  </Grid>
                </ThemeProvider>
              </CacheProvider>
            )}
          </Grid>
        )}
        <PrefillButton />
      </AccordionDetails>
    </Accordion>
  );
};
